/**
 * TemplatePreview — SÓ DESENHO.
 *
 * Não calcula mais nada: recebe o resultado pronto do renderizador único
 * (`renderTemplate`, em `@troia-v3/types`) e o desenha. Antes ele tinha um
 * estado interno (`testValues`) que só era preenchido quando a chave ainda
 * estava vazia — depois do primeiro valor digitado o balão congelava, porque
 * o valor novo chegava por `example` e nunca substituía o guardado. Sem
 * estado, não há o que congelar.
 *
 * O desenho do anexo (imagem, vídeo, documento, localização) já existia aqui e
 * nunca era alcançado: dependia de props que nenhum consumidor passava. Agora
 * ele é alimentado por `render.cabecalho` — inclusive com o NOME REAL do
 * arquivo, que antes era a palavra "documento.pdf" escrita no código.
 */
import React from 'react';
import type { TemplateRenderizado } from '@troia-v3/types';
import { Icon, IntegrationIcon } from '@/shared/icons';
import { integracaoDoProvider } from './templateRender';

interface TemplatePreviewProps {
  /** Resultado pronto de `renderTemplate` (via `renderTemplateSeguro`). */
  render: TemplateRenderizado | null;
  /** `providerType` do template — decide só o ícone mostrado acima do balão. */
  providerType?: string;
  /** Ocultar o cabeçalho "Pré-visualização do Template" (default: false). */
  hideHeader?: boolean;
  /** Balão ocupa 100% da largura + sem altura mínima fixa (default: false). */
  fullWidthBubble?: boolean;
}

/** Extensão em caixa alta a partir do nome do arquivo (ou da URL). */
function extensaoDoArquivo(nome?: string, url?: string): string {
  const alvo = nome || url || '';
  const semQuery = alvo.split('?')[0] ?? '';
  const ponto = semQuery.lastIndexOf('.');
  if (ponto === -1 || ponto === semQuery.length - 1) return 'Arquivo';
  return semQuery.slice(ponto + 1).toUpperCase();
}

/** Cartão do anexo do cabeçalho — imagem, vídeo, documento ou localização. */
const AnexoDoCabecalho: React.FC<{ cabecalho: NonNullable<TemplateRenderizado['cabecalho']> }> = ({
  cabecalho,
}) => {
  const { formato, url, nomeArquivo } = cabecalho;

  if (formato === 'localizacao') {
    return (
      <div
        className="rounded-lg overflow-hidden mb-1"
        style={{ backgroundColor: 'rgba(0,0,0,0.05)' }}
        data-testid="template-preview-anexo-localizacao"
      >
        <div
          className="h-32 flex items-center justify-center"
          style={{ backgroundColor: '#e8f5e9' }}
        >
          <div className="text-center">
            <Icon name="map-pin" className="w-8 h-8 mx-auto mb-1" style={{ color: '#e53935' }} />
            <p className="text-xs font-medium" style={{ color: '#333' }}>
              Localização
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (formato === 'documento') {
    return (
      <div
        className="rounded-lg overflow-hidden mb-1 p-3 flex items-center gap-3"
        style={{ backgroundColor: 'rgba(0,0,0,0.05)' }}
        data-testid="template-preview-anexo-documento"
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: '#4285f4', color: '#fff' }}
        >
          <Icon name="file" className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-sm font-medium truncate"
            style={{ color: '#000' }}
            data-testid="template-preview-anexo-nome"
          >
            {nomeArquivo || 'Arquivo do cabeçalho'}
          </p>
          <p className="text-xs" style={{ color: '#667781' }}>
            {extensaoDoArquivo(nomeArquivo, url)}
          </p>
        </div>
        <Icon name="download" className="w-5 h-5 flex-shrink-0" style={{ color: '#667781' }} />
      </div>
    );
  }

  if (formato === 'video') {
    return (
      <div
        className="rounded-lg overflow-hidden mb-1 h-40 flex items-center justify-center"
        style={{ backgroundColor: url ? '#000' : 'rgba(0,0,0,0.1)' }}
        data-testid="template-preview-anexo-video"
      >
        {url ? (
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(255,255,255,0.8)' }}
          >
            <Icon name="play" className="w-6 h-6 ml-0.5" style={{ color: '#000' }} />
          </div>
        ) : (
          <div className="text-center">
            <Icon name="video" className="w-8 h-8 mx-auto mb-1" style={{ color: '#667781' }} />
            <p className="text-xs" style={{ color: '#667781' }}>
              Vídeo
            </p>
          </div>
        )}
      </div>
    );
  }

  // Imagem
  if (url) {
    return (
      <div className="rounded-lg overflow-hidden mb-1" data-testid="template-preview-anexo-imagem">
        <img src={url} alt="Anexo do cabeçalho" className="w-full" />
      </div>
    );
  }

  return (
    <div
      className="rounded-lg overflow-hidden mb-1 h-40 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}
      data-testid="template-preview-anexo-imagem"
    >
      <div className="text-center">
        <Icon name="image" className="w-8 h-8 mx-auto mb-1" style={{ color: '#667781' }} />
        <p className="text-xs" style={{ color: '#667781' }}>
          Imagem
        </p>
      </div>
    </div>
  );
};

/** Botões do template (CTA e resposta rápida), como no WhatsApp real. */
const BotoesDoTemplate: React.FC<{
  botoes: NonNullable<TemplateRenderizado['botoes']>;
  cor: string;
}> = ({ botoes, cor }) => (
  <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }} data-testid="template-preview-botoes">
    {botoes.map((botao, indice) => (
      <div
        key={`${botao.texto}-${indice}`}
        className="flex items-center justify-center gap-1.5 py-2 text-sm font-medium"
        style={{
          color: cor,
          borderTop: indice > 0 ? '1px solid rgba(0,0,0,0.08)' : undefined,
        }}
        data-testid="template-preview-botao"
      >
        <Icon
          name={botao.tipo === 'URL' ? 'external-link' : botao.tipo === 'PHONE_NUMBER' ? 'phone' : 'corner-up-left'}
          className="w-3.5 h-3.5"
        />
        {botao.texto}
      </div>
    ))}
  </div>
);

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  render,
  providerType,
  hideHeader = false,
  fullWidthBubble = false,
}) => {
  const integracao = integracaoDoProvider(providerType);
  const ehEmail = render?.canal === 'email';
  const temConteudo =
    !!render
    && (!!render.corpo || !!render.corpoHtml || !!render.cabecalho || !!render.assunto || !!render.botoes);

  const desenhar = (): React.ReactNode => {
    if (!render) return null;

    if (render.canal === 'email') {
      return (
        <div
          className="rounded-lg border overflow-hidden"
          style={{ borderColor: '#d1d5db', backgroundColor: '#ffffff' }}
        >
          <div className="px-4 py-3 border-b" style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }}>
            <div className="flex gap-1.5 mb-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#ef4444' }} />
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#f59e0b' }} />
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#22c55e' }} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium" style={{ color: '#6b7280', minWidth: 60 }}>
                Assunto:
              </span>
              <span
                className="text-sm font-semibold"
                style={{ color: '#111827' }}
                data-testid="template-preview-assunto"
              >
                {render.assunto || '(sem assunto)'}
              </span>
            </div>
          </div>
          <div className="p-6 min-h-[160px]" style={{ backgroundColor: '#ffffff', color: '#1f2937' }}>
            {/* Com HTML, o e-mail aparece como o cliente vai receber — é o que a
                tela de campanha sempre mostrou. Sem HTML, sobra o texto puro.
                O valor das variáveis já vem escapado do renderizador único
                (`corpoHtml`), então dado do cliente nunca vira marcação. */}
            {render.corpoHtml ? (
              <div
                className="overflow-auto max-h-[420px]"
                data-testid="template-preview-corpo"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: render.corpoHtml }}
              />
            ) : (
              <div style={{ whiteSpace: 'pre-wrap' }} data-testid="template-preview-corpo">
                {render.corpo}
              </div>
            )}
          </div>
        </div>
      );
    }

    const ehMeta = render.canal === 'instagram' || render.canal === 'facebook';
    // O template sai de nós, então o balão fica do lado de quem envia — à
    // direita — nos quatro canais. Campanhas desenhava Instagram à esquerda.
    const fundo = ehMeta ? '#0084ff' : '#dcf8c6';
    const corDoTexto = ehMeta ? '#ffffff' : '#000000';
    const corSecundaria = ehMeta ? 'rgba(255,255,255,0.75)' : '#667781';

    return (
      <div
        className={`${fullWidthBubble ? 'w-full' : 'max-w-sm ml-auto'} rounded-lg rounded-tr-sm overflow-hidden`}
        style={{ backgroundColor: fundo }}
        data-testid="template-preview-balao"
      >
        {render.cabecalho && (
          <div className="px-1 pt-1">
            {render.cabecalho.formato === 'texto' ? (
              render.cabecalho.texto ? (
                <p
                  className="text-sm font-semibold px-2 pt-1"
                  style={{ color: corDoTexto }}
                  data-testid="template-preview-cabecalho-texto"
                >
                  {render.cabecalho.texto}
                </p>
              ) : null
            ) : (
              <AnexoDoCabecalho cabecalho={render.cabecalho} />
            )}
          </div>
        )}

        {render.corpo && (
          <div className="px-3 pb-2 pt-1">
            <p
              className="text-sm"
              style={{ whiteSpace: 'pre-wrap', color: corDoTexto }}
              data-testid="template-preview-corpo"
            >
              {render.corpo}
            </p>
          </div>
        )}

        {render.rodape && (
          <div className="px-3 pb-1">
            <p
              className="text-xs"
              style={{ color: corSecundaria }}
              data-testid="template-preview-rodape"
            >
              {render.rodape}
            </p>
          </div>
        )}

        <div className="px-3 pb-1.5 flex justify-end">
          <span className="text-[10px]" style={{ color: corSecundaria }}>
            12:00
          </span>
        </div>

        {render.botoes && render.botoes.length > 0 && (
          <BotoesDoTemplate botoes={render.botoes} cor={ehMeta ? '#ffffff' : '#00a5f4'} />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {!hideHeader && (
        <div className="flex items-center gap-3">
          <div
            className="p-2 rounded-lg"
            style={{
              backgroundColor: 'var(--theme-primary-background)',
              color: 'var(--theme-primary)',
            }}
          >
            <Icon name="eye" className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold" style={{ color: 'var(--theme-card-text)' }}>
              Pré-visualização do Template
            </h3>
            <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
              Veja como o template aparecerá para seus clientes
            </p>
          </div>
        </div>
      )}

      <div>
        <div className="mb-3 flex items-center gap-2">
          <IntegrationIcon id={integracao} size={16} variant="brand" />
          <h4 className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
            Preview {ehEmail ? 'do E-mail' : 'da Mensagem'}
          </h4>
        </div>

        <div
          className={`rounded-lg border p-6 ${fullWidthBubble ? '' : 'min-h-[300px]'}`}
          style={{
            backgroundColor: ehEmail ? '#f9fafb' : 'var(--theme-input-background)',
            borderColor: 'var(--theme-card-border)',
          }}
          data-testid="template-preview-conteudo"
        >
          {temConteudo ? (
            desenhar()
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <IntegrationIcon id={integracao} size={48} variant="mono" className="mb-3 opacity-30" />
              <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                Nenhum conteúdo para visualizar
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                Preencha o formulário para ver o preview
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
